// LineChart.js
// Renders the chart it is given. Saved display settings (title, axes, colours)
// are applied by the caller — applyChartSettings, with the settings of the
// version the shown run executed — so this component reads nothing itself.
//
// The figure (traces + layout) comes from a pure builder so the combined
// chart's whole-image export can draw this chart without rendering it.
import React from 'react';
import Plot from 'react-plotly.js';
import { useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import ChartCard from './ChartCard';
import ChartExportButton from './ChartExportButton';
import { resolveSeriesLabels } from '../../utils/chartSeries';
import { downloadChartPng, exportTitle } from '../../utils/chartExport';
import {
  CHART_CONFIG,
  HOVER_UNIFIED,
  LABEL_GUTTER,
  LINE_WIDTH,
  POINT_SIZE,
  buildAxes,
  buildDirectLabels,
  buildLayout,
  chartAxisError,
  hoverValues,
  seriesColor,
  seriesDash,
  seriesSymbol,
} from '../../utils/chartTheme';

// Points are {x, y} maps (the Firestore-safe shape); artifacts stored before
// that fix carry [x, y] pairs, so both shapes must render.
const pointX = (p) => (Array.isArray(p) ? p[0] : p?.x);
const pointY = (p) => (Array.isArray(p) ? p[1] : p?.y);

export const buildLineFigure = (chartData, { ink, themePalette }) => {
  const chartColors = chartData.colors || themePalette;
  const showPoints = chartData.show_points !== false;
  const lineWidth = chartData.line_width || LINE_WIDTH;
  const showGrid = chartData.grid !== false;
  const showLegend = chartData.show_legend !== false;

  // One element is one line, so the element labels ARE the legend's entries and
  // are what the placeholder fallback has to rewrite.
  const { labels: seriesLabels, hideLegend } = resolveSeriesLabels(
    chartData.elements.map((el) => el.label)
  );

  // A series past the palette's last slot takes a tinted colour AND a different
  // dash and marker, so it never reads as a repeat of an earlier line.
  const data = chartData.elements.map((line, index) => ({
    type: 'scatter',
    mode: showPoints ? 'lines+markers' : 'lines',
    // Match emailed charts while retaining missing observations in the data.
    connectgaps: true,
    name: seriesLabels[index],
    x: line.points.map(pointX),
    y: line.points.map(pointY),
    line: {
      color: seriesColor(chartColors, index),
      width: lineWidth,
      dash: seriesDash(chartColors, index),
    },
    marker: showPoints ? {
      size: POINT_SIZE,
      color: seriesColor(chartColors, index),
      symbol: seriesSymbol(chartColors, index),
    } : undefined,
    customdata: hoverValues(line.points.map(pointY), chartData.y_label, chartData.y_unit),
    hovertemplate: HOVER_UNIFIED
  }));

  // Labelling each line at its end beats a legend the eye has to shuttle to,
  // but only where the lines end far enough apart to be labelled separately;
  // `buildDirectLabels` returns nothing when they do not, and the right-hand
  // gutter is only reserved when there is something to put in it.
  const directLabels = buildDirectLabels({
    series: data, palette: chartColors, ink,
    xScale: chartData.x_scale, yScale: chartData.y_scale,
  });

  const layout = buildLayout({
    axes: buildAxes({
      categoryLabel: chartData.x_label,
      measureLabel: chartData.y_label,
      categoryUnit: chartData.x_unit,
      measureUnit: chartData.y_unit,
      categoryRotation: chartData.x_axis_rotation || 0,
      measureRotation: chartData.y_axis_rotation || 0,
      showGrid,
      autoScale: chartData.auto_scale,
      categoryMetadata: {
        scale: chartData.x_scale,
        ticks: chartData.x_ticks,
        tickLabels: chartData.x_tick_labels,
      },
      measureMetadata: {
        scale: chartData.y_scale,
        ticks: chartData.y_ticks,
        tickLabels: chartData.y_tick_labels,
      },
      ink
    }),
    // An explicit `show_legend: false` and a lone placeholder series each mean
    // "no legend"; either one alone is enough to drop it.
    showLegend: showLegend && !hideLegend,
    ink,
    annotations: directLabels,
    labelGutter: directLabels.length ? LABEL_GUTTER : 0,
    // One box for the whole x position: reading three series at one date should
    // not mean chasing three separate tooltips.
    hovermode: 'x unified'
  });

  return { data, layout };
};

// `showTitle: false` is for a frame that prints the title itself (a View
// card); the export still carries it. `ancestorTitles` are the titles of the
// combined charts this one sits inside, printed on the export as a prefix.
function LineChartComponent({ chartData, showTitle = true, ancestorTitles = [] }) {
  const theme = useTheme();
  // The series palette and the reading ink both come from the app theme, so the
  // chart layer holds no colour of its own.
  const ink = { primary: theme.palette.text.primary, secondary: theme.palette.text.secondary };
  const axisError = chartAxisError(chartData);
  const figure = axisError ? null : buildLineFigure(chartData, { ink, themePalette: theme.palette.custom.chartColors });
  const title = exportTitle(ancestorTitles, chartData.title);

  return (
    <ChartCard
      title={showTitle ? chartData.title : null}
      ink={ink}
      actions={figure && <ChartExportButton onExport={() => downloadChartPng({ ...figure, title })} />}
    >
      {axisError ? (
        <Alert severity="error">{axisError}</Alert>
      ) : (
        <Plot
          data={figure.data}
          layout={figure.layout}
          config={CHART_CONFIG}
          style={{ width: '100%', height: 'calc(100% - 50px)' }}
          useResizeHandler={true}
        />
      )}
    </ChartCard>
  );
}

export default LineChartComponent;
